import React, { createContext, useContext } from "react";
import { Constants, Enums, Helpers, Store } from "utils";
import { Dark, Light } from "themes";
import { Theme } from "themes/interfaces";
import { IUser } from "interfaces";
import { CheckIn, Feedback } from "types";
import { initializeService, IProvidedService } from "services";

interface ProvidedValueType {
  user: IUser;
  setUser: (user: any) => void;
  t: any;
  locale: string;
  locales: any;
  setLocale: (language: string) => void;
  theme: Theme;
  setTheme: (theme: Theme) => void;
  toggleTheme: () => void;
  feedback: Feedback | null;
  setFeedback: (feedback: Feedback | null) => void;
  service: IProvidedService;
  // checkInClinic: number | string;
  // checkInStatus: number;
  // setCheckIn: (checkIn: CheckIn | null) => void;
}

const initialState = {
  theme: Dark,
  locale: "EN",
  translations: Constants.LOCALE_STRINGS[Constants.REGIONS.EN],
  locales: Object.keys(Constants.REGIONS),
  user: Store.sessionStorage.get() as IUser,
  feedback: null,
  // checkInClinic: "",
  // checkInStatus: Enums.EnumAppointmentStatus.None,
};

export const AppContext = createContext<ProvidedValueType>({
  user: initialState.user,
  setUser: () => {},
  t: initialState.translations,
  locale: initialState.locale,
  locales: initialState.locales,
  setLocale: () => {},
  theme: initialState.theme,
  setTheme: () => {},
  toggleTheme: () => {},
  feedback: initialState.feedback,
  setFeedback: () => {},
  service: {} as IProvidedService,
  // checkInClinic: initialState.checkInClinic,
  // checkInStatus: initialState.checkInStatus,
  // setCheckIn: () => {},
});

interface Props {
  initTheme: Theme;
  initLocale: string;
  children?: React.ReactNode;
}

export const AppProvider = React.memo<Props>(
  ({ initTheme, initLocale, children }) => {
    const [user, setUser] = React.useState<IUser>(initialState.user);
    // TODO: think about a better solution
    const [service, setService] = React.useState<IProvidedService>(
      initializeService(user?.wq ?? "")
    );
    const [theme, setTheme] = React.useState<Theme>(initTheme);
    const [locale, setLocale] = React.useState<string>(initLocale);
    const [feedback, setFeedback] = React.useState<Feedback | null>(
      initialState.feedback
    );

    // const [checkIn, setCheckIn] = React.useState<CheckIn | null>(null);
    // const [checkInClinic, setCheckInClinic] = React.useState<number | string>(
    //   initialState.checkInClinic
    // );
    // const [checkInStatus, setCheckInStatus] = React.useState<number>(
    //   initialState.checkInStatus
    // );

    const setUserCallback = React.useCallback((newUser: IUser | null) => {
      setUser((currentUser: IUser | null) => {
        const user = {
          ...currentUser,
          ...newUser,
        };
        if (newUser === null) {
          Store.sessionStorage.clear();
        } else {
          const username = Helpers.replaceComma(user.username ?? "");
          Store.sessionStorage.set({ ...user, username });
        }
        // TODO: think about a better solution
        setService(() => initializeService(user?.wq ?? ""));
        return Store.sessionStorage.get();
      });
    }, []);

    const setLocaleCallback = React.useCallback((newLocale: string) => {
      setLocale((currentLocale: string) =>
        currentLocale === newLocale ? currentLocale : newLocale
      );
    }, []);

    const setThemeCallback = React.useCallback((newTheme: Theme) => {
      setTheme((currentTheme: Theme) =>
        currentTheme.id === newTheme.id ? currentTheme : newTheme
      );
    }, []);

    const setFeedbackCallback = React.useCallback(
      (newFeedback: Feedback | null) => {
        setFeedback((currentFeedback: Feedback | null) => newFeedback);
      },
      []
    );

    // const setCheckInCallback = React.useCallback(
    //   (newCheckIn: CheckIn | null) => {
    //     setCheckIn((currentCheckIn: CheckIn | null) => {
    //       // TODO: think about a better solution
    //       if (newCheckIn === null) {
    //         setCheckInClinic(initialState.checkInClinic);
    //         setCheckInStatus(initialState.checkInStatus);
    //       } else {
    //         setCheckInClinic(newCheckIn.id);
    //         setCheckInStatus(newCheckIn.status);
    //       }
    //       return newCheckIn;
    //     });
    //   },
    //   []
    // );

    const toggleThemeCallback = React.useCallback(() => {
      setTheme((currentTheme) => (currentTheme.id === Dark.id ? Light : Dark));
    }, []);

    const MemoizedValue = React.useMemo(() => {
      const value: ProvidedValueType = {
        theme,
        setTheme: setThemeCallback,
        toggleTheme: toggleThemeCallback,
        locale,
        t: Constants.LOCALE_STRINGS[locale],
        locales: Constants.LOCALE_STRINGS[locale],
        setLocale: setLocaleCallback,
        user,
        setUser: setUserCallback,
        feedback,
        setFeedback: setFeedbackCallback,
        service,
        // checkInClinic,
        // checkInStatus,
        // setCheckIn: setCheckInCallback,
      };
      return value;
    }, [
      theme,
      setThemeCallback,
      toggleThemeCallback,
      locale,
      setLocaleCallback,
      user,
      setUserCallback,
      feedback,
      setFeedbackCallback,
      service,
      // checkInClinic,
      // checkInStatus,
      // setCheckInCallback,
    ]);

    return (
      <AppContext.Provider value={MemoizedValue}>
        {children}
      </AppContext.Provider>
    );
  }
);

export const useApp = () => useContext(AppContext);
